import { MAX_SLOTS } from "./BoatTree";
import { Cannon } from "./Gunnery";
import { shortestAngle } from "./Sailing";

/**
 * How a gun is laid on a target: the arc it may swing through, how fast it gets there, and
 * which gun a captain's aim actually belongs to.
 *
 * **This is the half of combat that is not the hull.** A bearing used to be the deck plan's and
 * nothing else — a gun was welded to its slot and the only aiming was turning the ship. It
 * swings now (design/Controls.md): the pointer names a direction, every gun tries to come onto
 * it, and each is stopped by its own arc and its own traverse rate. The rate has been authorable
 * on the gun ladder since before anything read it (`CannonStats.rotationSpeed`); this is the
 * code that finally does.
 *
 * It lives in `shared/` for the reason everything here does: the server owns where a ball goes,
 * and every client draws the barrel it came out of. Two implementations of "how far a gun has
 * swung" would drift, and drift here is a ball leaving a barrel that is visibly pointing
 * somewhere else.
 */

/**
 * How far a gun may swing off the bearing its slot was authored at, in radians, either way.
 *
 * A quarter turn each way: enough that a gun covers a real spread of the compass, and little
 * enough that **which rails a hull has is still the question the deck plan answers**. One gun
 * reaches a semicircle, never more, so what a ship can shoot at is the union of its slots'
 * semicircles — and that is a property of the tree in `config/boat.json`, not of this constant.
 *
 * The shipped tree makes that the spine of growth: hull 1 carries a single chase gun at the bow,
 * so a fresh captain can only shoot into the half of the world they are sailing towards and has
 * to run a target down to kill it. Hull 2 adds a stern slot, and only then can a ship answer
 * something on its tail. Sailing is still the combat (design/Pitch.md) — free aim widened what a
 * gun can reach without touching what a *hull* can reach.
 *
 * It is a constant rather than a per-rung dial because it is a rule about the game, not a
 * property of a gun — widening it is a decision about how much free aim the pitch tolerates,
 * and that is one number for the whole arena. The dial that does vary per rung is how *fast*
 * a gun crosses the arc.
 */
export const GUN_ARC = Math.PI / 2;

/**
 * How long a gun is thrown back by its own shot, and how long it takes to run back out — in
 * seconds. The kick is a snap and the return is a settle, which is the whole shape of a recoil:
 * a symmetric bob reads as a spring rather than as a gun going off.
 *
 * **One pair for every cannon in the game.** A per-rung dial would be a fourth number on a page
 * that already has range, shot speed, cooldown and traverse, tuned to make the same animation
 * look the same — and the thing that actually differs between a swivel and a long gun is how
 * often it goes off, which is already the reload. If a heavy gun should sit out longer than a
 * light one, that is a `cooldown`, not a recoil.
 *
 * The total is deliberately a small fraction of the shortest sensible reload: a quarter second
 * against a default of one. It is long enough to read at a glance and short enough that the
 * rotation lock below costs a captain a beat rather than a fight.
 */
export const RECOIL_KICK_SECONDS = 0.05;
export const RECOIL_RETURN_SECONDS = 0.2;
/** The whole animation, and exactly how long a gun's rotation stays locked. */
export const RECOIL_SECONDS = RECOIL_KICK_SECONDS + RECOIL_RETURN_SECONDS;

/**
 * How far back a gun is thrown, as a fraction of its own **drawn** length.
 *
 * A fraction rather than a pixel count, for the reason `CannonType.muzzleX` is one: a gun
 * scaled up by its rung or by its slot has to recoil by proportionally more, or a Galleon's
 * long guns twitch while a swivel bucks. It is still one constant for every cannon — what
 * varies is the gun it is measured against, not the setting.
 */
export const RECOIL_TRAVEL = 0.22;

/**
 * Where a battery is pointed and how it gets there: one offset per cannon slot, in radians
 * from that slot's authored bearing, always inside [-GUN_ARC, +GUN_ARC].
 *
 * **Offsets rather than bearings**, and that is the whole reason this survives a refit. A ship
 * grows mid-battle (design/Evolution.md) — a hull upgrade re-authors every slot on the deck, and
 * a gun upgrade changes how fast the one in that slot swings — and an offset stays meaningful
 * through both: a gun trained hard forward is still trained hard forward on the bigger hull, and
 * a rail that was empty a second ago starts at rest, which is what `0` already means. Storing
 * absolute bearings would need a rebuild keyed to what changed, and getting that wrong is guns
 * snapping across the deck on the frame a point is spent.
 *
 * Sized at MAX_SLOTS rather than at the hull's slot count, for the same reason the server's
 * reload clocks are: growing into a bigger hull neither reallocates nor has to decide what a
 * freshly-appeared slot's aim should be.
 *
 * Every ship in the arena has one — the server's, which is authoritative and fires the guns, and
 * one per boat on every client, which is only ever drawn. They run the same integration from the
 * same `aim` on the wire, so they agree without being synced.
 */
export class Battery {
    private readonly _offsets = new Float64Array(MAX_SLOTS);
    /**
     * Seconds left of each gun's recoil, counted down by `train`. Zero is a gun run out and
     * free to turn.
     *
     * It lives here, beside the offsets, rather than in whatever is drawing the sprite, because
     * a recoil is **not only an animation**: a gun thrown back on its mount does not traverse,
     * so this is what stops it. The server runs the same countdown off the same constants, and
     * has to — a client that locked a gun the arena was still swinging would draw a barrel
     * pointing somewhere the ball did not go. See design/Controls.md.
     */
    private readonly _recoil = new Float64Array(MAX_SLOTS);

    /** How far this gun has swung off its slot, in radians. Zero for a slot nothing has aimed. */
    public offsetAt(index: number): number {
        return index >= 0 && index < MAX_SLOTS ? this._offsets[index] : 0;
    }

    /**
     * Throws one gun back on its mount. Called on **both** ends when a ball actually leaves it —
     * the server as it fires, every client as the `shot` broadcast arrives — so the lock below
     * begins at the same event rather than at two different guesses about one.
     */
    public kick(index: number): void {
        if (index >= 0 && index < MAX_SLOTS) this._recoil[index] = RECOIL_SECONDS;
    }

    /**
     * How far back this gun currently sits, from 0 run out to 1 at the end of its travel. Purely
     * for whoever is drawing the barrel; nothing in the simulation reads it.
     *
     * Out fast and back slow. The kick is linear, because a gun going off is a snap and easing
     * into one only softens it. The run-out is cubed on the time **remaining**, which leaves the
     * back stop quickly and arrives at the front one with no speed at all — a carriage settling
     * onto its breeching rather than stopping dead against it.
     */
    public recoilOf(index: number): number {
        if (index < 0 || index >= MAX_SLOTS) return 0;

        const left = this._recoil[index];
        if (left <= 0) return 0;
        if (left > RECOIL_RETURN_SECONDS) return (RECOIL_SECONDS - left) / RECOIL_KICK_SECONDS;

        const settling = left / RECOIL_RETURN_SECONDS;
        return settling * settling * settling;
    }

    /** Where a gun actually points, in radians **relative to the bow**. What a ball leaves on. */
    public bearingOf(cannon: Cannon): number {
        return cannon.bearing + this.offsetAt(cannon.index);
    }

    /**
     * Swings every gun in `roster` toward `aim` — a direction **relative to the bow**, which is
     * what the pointer means once the hull's own heading is taken out of it.
     *
     * Every gun tracks, not just the one that will fire. A captain reads a ship's threat off
     * where its barrels are pointing, and a deck where seven guns sit at rest while one quietly
     * lines up says nothing; it also means the gun that fires next is already most of the way
     * onto the target rather than starting from its slot. Guns that cannot reach the aim stop at
     * the edge of their arc and sit there, which is the picture a captain needs — *that* side is
     * out of the fight until the hull comes about.
     *
     * A gun with a `rotationSpeed` of zero never leaves its slot, which is exactly what every
     * gun did before this existed.
     */
    public train(roster: readonly Cannon[], aim: number, deltaSeconds: number): void {
        for (const cannon of roster) {
            // A gun still thrown back by its own shot cannot be laid: the crew are hauling it
            // out, not training it. That is a real cost of firing rather than a flourish — it
            // is why a captain who empties a broadside at one bearing cannot instantly bring
            // the same guns onto another. See RECOIL_SECONDS.
            if (this._recoil[cannon.index] > 0) {
                this._recoil[cannon.index] = Math.max(0, this._recoil[cannon.index] - deltaSeconds);
                continue;
            }

            const target = reachableOffset(cannon, aim);
            const current = this._offsets[cannon.index];
            const delta = target - current;
            // Plain subtraction rather than `shortestAngle`: both ends are offsets inside one
            // arc, never headings, so there is no long way round for a gun to take.
            this._offsets[cannon.index] = current + Math.sign(delta) * Math.min(Math.abs(delta), cannon.traverse * deltaSeconds);
        }
    }

    /**
     * The gun to fire at `aim`, again relative to the bow: whichever loaded one is pointing
     * closest to it **right now**.
     *
     * Right now, not "whichever could get there" — a gun already on the target beats one that
     * would be better once it arrives, which is what makes a click feel like it went where it
     * was pointed. With the whole battery tracking, the two answers converge within a traverse
     * anyway; they differ exactly while the ship is coming about, which is when the difference
     * is a ball down the bearing the player is watching instead of one fired sideways.
     *
     * **A loaded gun is always returned, even one that cannot come near the aim.** A hull whose
     * only slot is a bow chaser — which is what the shipped tree spawns you in — answers a click
     * astern with that gun at the edge of its travel, a quarter turn off, and the ball goes
     * where the barrel is rather than where the pointer is. That is the design: a captain who
     * clicks gets a ball, and how badly it missed is the feedback that the ship is pointing the
     * wrong way. Null only when the whole battery is reloading, or there is no battery.
     *
     * Recoil is **not** consulted: it is far shorter than any sensible reload, so a gun still
     * running out is one the caller's own reload clock has already ruled out. Testing it here
     * would only matter for a tree tuned to a reload shorter than a quarter second, where the
     * honest answer is that the tree is asking for a machine gun.
     */
    public pick(roster: readonly Cannon[], aim: number, isLoaded: (cannon: Cannon) => boolean): Cannon | null {
        let best: Cannon | null = null;
        let bestError = Infinity;

        for (const cannon of roster) {
            if (!isLoaded(cannon)) continue;

            const error = Math.abs(shortestAngle(this.bearingOf(cannon), aim));
            if (error >= bestError) continue;

            best = cannon;
            bestError = error;
        }

        return best;
    }
}

/**
 * The offset this gun would need to point at `aim`, clamped to what its mount allows. The one
 * place the arc is applied, so the drawn barrel, the fired ball and the gun the click chose all
 * stop at the same angle.
 *
 * `shortestAngle` rather than a subtraction, because `aim` is a heading difference that may have
 * come round the back of the compass: a starboard gun asked to fire at -175° should read that as
 * 95° past its slot — out of reach by five degrees — rather than as 265° the other way.
 */
export function reachableOffset(cannon: Cannon, aim: number): number {
    return Math.max(-GUN_ARC, Math.min(GUN_ARC, shortestAngle(cannon.bearing, aim)));
}
